import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Collapse, Row } from 'reactstrap';
import api from '~/services/api';
import { formatPrice } from '~/util/format';
import useRoles from '~/hooks/useRoles';

const CAUSE_TEXT = {
  COURIER_VEHICLE_BLOCKED_LIMIT:
    'Limite de entregadores com veículo não permitido atingido',
  COURIER_MISSING_PLATE_LIMIT: 'Limite de entregadores sem placa atingido',
  COURIER_BLACKLISTED_LIMIT: 'Limite de entregadores bloqueados atingido',
  DRIVER_CANCEL_LIMIT: 'Entregadores cancelaram repetidamente',
  DISPATCH_FAILED: 'Transportadora não aceitou a solicitação',
  PARTNER_REQUEUE_LIMIT: 'Limite de rebuscas do parceiro atingido',
  NO_DESTINATION: 'Pedido sem endereço de entrega',
};

const causeText = (item, fallback) => {
  const cause = item.metadata && item.metadata.cause;
  return CAUSE_TEXT[cause] || fallback;
};

const EVENT_META = {
  DISPATCHED: {
    icon: 'la-search',
    color: '#6B7280',
    title: 'Buscando entregador',
    text: () => '',
  },
  INCENTIVE_APPLIED: {
    icon: 'la-dollar-sign',
    color: '#0EA5E9',
    title: 'Incentivo aplicado',
    text: (_op, item) =>
      `Incentivo de ${formatPrice(item.incentive || 0)}${
        item.metadata && item.metadata.retry_index
          ? ` na ${item.metadata.retry_index}ª rebusca`
          : ''
      }`,
  },
  DRIVER_ACCEPTED: {
    icon: 'la-check-circle',
    color: '#22C55E',
    title: 'Entregador aceitou',
    text: (_op, item) =>
      item.worker_name
        ? `${item.worker_name} aceitou a corrida`
        : 'Um entregador aceitou',
  },
  DRIVER_CANCELED: {
    icon: 'la-times-circle',
    color: '#EF4444',
    title: 'Entregador cancelou',
    text: (_op, item) =>
      item.worker_name
        ? `${item.worker_name} cancelou a corrida`
        : 'O entregador cancelou',
  },
  ACCEPT_TIMEOUT: {
    icon: 'la-hourglass-end',
    color: '#F59E0B',
    title: 'Entregador não iniciou a coleta',
    text: (_op, item) =>
      item.worker_name
        ? `${item.worker_name} aceitou mas não iniciou a coleta em 60 minutos — entrega cancelada e refeita na mesma transportadora`
        : 'Entregador aceitou mas não iniciou a coleta em 60 minutos — entrega cancelada e refeita na mesma transportadora',
  },
  COURIER_BLACKLISTED: {
    icon: 'la-user-slash',
    color: '#EF4444',
    title: 'Entregador bloqueado',
    text: (_op, item) =>
      item.worker_name
        ? `${item.worker_name} está na lista de bloqueio — entrega cancelada e refeita na mesma transportadora`
        : 'Entregador na lista de bloqueio — entrega cancelada e refeita na mesma transportadora',
  },
  COURIER_VEHICLE_BLOCKED: {
    icon: 'la-bicycle',
    color: '#EF4444',
    title: 'Veículo não permitido',
    text: (_op, item) =>
      `${
        item.worker_name ? `${item.worker_name} — ` : ''
      }bicicleta, patinete ou a pé não são aceitos. Entrega cancelada e refeita na mesma transportadora`,
  },
  COURIER_MISSING_PLATE: {
    icon: 'la-id-card',
    color: '#EF4444',
    title: 'Entregador sem placa',
    text: (_op, item) =>
      `${
        item.worker_name ? `${item.worker_name} — ` : ''
      }veículo sem placa informada. Entrega cancelada e refeita na mesma transportadora`,
  },
  PARTNER_REQUEUED: {
    icon: 'la-sync',
    color: '#F59E0B',
    title: 'Parceiro reiniciou a busca',
    text: () =>
      'A transportadora voltou a procurar entregador na mesma entrega',
  },
  OPERATOR_ADVANCED: {
    icon: 'la-exchange-alt',
    color: '#F59E0B',
    title: 'Troca de transportadora',
    text: (_op, item) => {
      const meta = item.metadata || {};
      const route = meta.from && meta.to ? ` (${meta.from} → ${meta.to})` : '';
      return `${causeText(
        item,
        'Sem entregador'
      )} — mudou de transportadora${route}`;
    },
  },
  SEARCH_FAILED: {
    icon: 'la-redo-alt',
    color: '#F59E0B',
    title: 'Nova tentativa',
    text: (_op, item) => {
      const cause = item.metadata && item.metadata.cause;
      if (cause === 'PARTNER_CANCELED') {
        return 'Cancelado pelo painel do parceiro, refazendo a busca';
      }
      return 'Sem entregador, refazendo a busca';
    },
  },
  MANUAL_SWITCH: {
    icon: 'la-user-cog',
    color: '#8B5CF6',
    title: 'Troca manual (suporte)',
    text: (_op, item) =>
      `Suporte trocou de transportadora${
        item.incentive
          ? ` e aplicou incentivo de ${formatPrice(item.incentive)}`
          : ''
      }`,
  },
  EXHAUSTED: {
    icon: 'la-exclamation-triangle',
    color: '#B45309',
    title: 'Sem transportadora disponível',
    text: (_op, item) =>
      `${causeText(
        item,
        'Todas as opções foram tentadas sem sucesso'
      )}. Entrega cancelada — é necessária ação manual`,
  },
  SESSION_CANCELED: {
    icon: 'la-ban',
    color: '#6B7280',
    title: 'Busca cancelada',
    text: () => 'A busca foi cancelada manualmente',
  },
  ADDRESS_CHANGED: {
    icon: 'la-map-marked-alt',
    color: '#8B5CF6',
    title: 'Endereço de destino alterado',
    text: (_op, item) => {
      const meta = item.metadata || {};

      if (!meta.new_address) {
        return 'Endereço de destino atualizado';
      }

      return (
        <>
          {meta.old_address && (
            <div>
              <span className="text-muted">De: </span>
              <span style={{ textDecoration: 'line-through' }}>
                {meta.old_address}
              </span>
            </div>
          )}
          <div>
            <span className="text-muted">Para: </span>
            <b>{meta.new_address}</b>
          </div>
          {(meta.user_name || meta.pin_manual) && (
            <div className="font-size-12 text-muted">
              {meta.user_name ? `Alterado por: ${meta.user_name}` : ''}
              {meta.user_name && meta.pin_manual ? ' - ' : ''}
              {meta.pin_manual ? 'pin ajustado manualmente' : ''}
            </div>
          )}
        </>
      );
    },
  },
};

const STATUS_META = {
  SEARCHING: {
    icon: 'la-search',
    color: '#6B7280',
    title: 'Buscando entregador',
  },
  GOING_TO_STORE: {
    icon: 'la-store',
    color: '#3B82F6',
    title: 'A caminho da loja',
  },
  ARRIVED_AT_STORE: {
    icon: 'la-store-alt',
    color: '#3B82F6',
    title: 'Chegou na loja',
  },
  ON_DELIVERY: {
    icon: 'la-motorcycle',
    color: '#0EA5E9',
    title: 'Em rota de entrega',
  },
  ARRIVED_AT_DESTINATION: {
    icon: 'la-map-marker',
    color: '#0EA5E9',
    title: 'Chegou no destino',
  },
  DELIVERED: { icon: 'la-box-open', color: '#22C55E', title: 'Entregue' },
  CANCELED: {
    icon: 'la-times-circle',
    color: '#EF4444',
    title: 'Entrega cancelada',
  },
  RETURNING_TO_STORE: {
    icon: 'la-undo',
    color: '#F59E0B',
    title: 'Retornando à loja',
  },
  NOT_DELIVERED: {
    icon: 'la-exclamation-circle',
    color: '#EF4444',
    title: 'Não entregue',
  },
};

// Correios não tem entregador: os mesmos status têm outro significado.
// "delivery" = entrega (ida) | "return" = devolução (logística reversa).
const CORREIOS_STATUS_META = {
  delivery: {
    SEARCHING: {
      icon: 'la-box',
      color: '#6B7280',
      title: 'Aguardando postagem',
    },
    ON_DELIVERY: {
      icon: 'la-truck',
      color: '#0EA5E9',
      title: 'Em transporte',
    },
    PENDING_CLIENT: {
      icon: 'la-store-alt',
      color: '#F59E0B',
      title: 'Aguardando retirada na agência',
    },
    RETURNED: {
      icon: 'la-undo',
      color: '#F59E0B',
      title: 'Devolvido ao remetente',
    },
  },
  return: {
    SEARCHING: {
      icon: 'la-box',
      color: '#6B7280',
      title: 'Devolução: aguardando postagem pelo cliente',
    },
    ON_DELIVERY: {
      icon: 'la-truck',
      color: '#0EA5E9',
      title: 'Devolução: em trânsito para a loja',
    },
    PENDING_CLIENT: {
      icon: 'la-store-alt',
      color: '#F59E0B',
      title: 'Devolução: aguardando retirada na agência',
    },
    DELIVERED: {
      icon: 'la-box-open',
      color: '#22C55E',
      title: 'Devolução: recebida pela loja',
    },
    RETURNED: {
      icon: 'la-undo',
      color: '#F59E0B',
      title: 'Devolução: objeto devolvido ao cliente',
    },
    CANCELED: {
      icon: 'la-times-circle',
      color: '#EF4444',
      title: 'Devolução cancelada',
    },
  },
};

function resolveStatusMeta(type, operator, blockKind) {
  const isCorreios =
    String(operator || '')
      .trim()
      .toUpperCase() === 'CORREIOS';

  if (isCorreios) {
    const correiosMeta =
      CORREIOS_STATUS_META[blockKind === 'return' ? 'return' : 'delivery'][
        type
      ];

    if (correiosMeta) return correiosMeta;
  }

  return STATUS_META[type];
}

function formatDateTime(at) {
  if (!at) return '';
  const [date, time] = at.split(' ');
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}, ${time || ''}`.trim();
}

function resolveItem(rawItem, operator, blockKind) {
  if (rawItem.kind === 'router_event') {
    const meta = EVENT_META[rawItem.type];
    if (!meta) return null;

    // Não exibe "Incentivo de R$ 0,00" — evento sem valor real.
    if (rawItem.type === 'INCENTIVE_APPLIED' && !rawItem.incentive) {
      return null;
    }

    return {
      icon: meta.icon,
      color: meta.color,
      title: meta.title,
      text: meta.text(operator, rawItem),
      at: rawItem.at,
    };
  }

  const meta = resolveStatusMeta(rawItem.type, operator, blockKind);
  if (!meta) return null;

  return {
    icon: meta.icon,
    color: meta.color,
    title: meta.title,
    text:
      rawItem.metadata && rawItem.metadata.reason
        ? rawItem.metadata.reason
        : '',
    at: rawItem.at,
  };
}

export default function DeliveryHistoryCard({ saleId, role, isForDelivery }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const { isSupport } = useRoles();

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `${role}/carts/${saleId}/delivery/history`
        );
        if (active) setHistory(data.history || []);
      } catch (err) {
        if (active) setHistory([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    // As rotas do Roteador de Entregas existem apenas no grupo do
    // administrator; nos demais perfis a chamada retorna 404.
    if (saleId && role === 'administrator' && isForDelivery) load();

    return () => {
      active = false;
    };
  }, [saleId, role, isForDelivery]);

  // Não renderiza pra usuários comuns
  if (!isSupport || !isForDelivery) return null;

  if (!loading && !history.length) return null;

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => setExpanded(v => !v)}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 justify-content-between">
              <div className="px-0 mb-0">Histórico de Entrega</div>
              <div className="px-0 mb-0">
                <i
                  className={`las ${
                    expanded ? 'la-angle-up' : 'la-angle-down'
                  } font-size-20`}
                />
              </div>
            </Row>
          </Col>
        </Row>

        <Collapse isOpen={expanded}>
          <div className="mt-3">
            {loading ? (
              <div className="text-muted font-size-14 py-2">Carregando...</div>
            ) : (
              history.map((block, bi) => (
                <div key={block.delivery_id} className={bi > 0 ? 'mt-4' : ''}>
                  <Row className="w-100 mx-0 mb-2">
                    <Col className="px-0">
                      <div className="font-size-16 font-weight-600 text-gray-700">
                        {block.operator}
                      </div>
                      {block.worker_name && (
                        <div className="font-size-13 text-muted">
                          {block.worker_name}
                        </div>
                      )}
                      {block.vehicle_plate && (
                        <div className="font-size-12 text-muted">
                          Placa: {block.vehicle_plate}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <div style={{ position: 'relative' }}>
                    {block.items.map((rawItem, idx) => {
                      const item = resolveItem(
                        rawItem,
                        block.operator,
                        block.kind
                      );
                      if (!item) return null;

                      const isLast = idx === block.items.length - 1;
                      const itemKey = `${rawItem.type}-${rawItem.at}-${idx}`;

                      return (
                        <Row
                          key={itemKey}
                          className="w-100 mx-0 flex-nowrap"
                          style={{
                            position: 'relative',
                            paddingBottom: isLast ? 0 : 18,
                          }}
                        >
                          {!isLast && (
                            <span
                              style={{
                                position: 'absolute',
                                left: 15,
                                top: 32,
                                bottom: 0,
                                width: 2,
                                background: '#E5E7EB',
                              }}
                            />
                          )}

                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mr-3"
                            style={{
                              width: 32,
                              height: 32,
                              minWidth: 32,
                              background: `${item.color}22`,
                            }}
                          >
                            <i
                              className={`las ${item.icon} font-size-18`}
                              style={{ color: item.color }}
                            />
                          </span>

                          <Col className="px-0">
                            <div className="font-size-14 font-weight-600 text-gray-700">
                              {item.title}
                            </div>
                            {item.text ? (
                              <div className="font-size-13 text-muted">
                                {item.text}
                              </div>
                            ) : null}
                            <div className="font-size-12 text-muted">
                              {formatDateTime(item.at)}
                            </div>
                          </Col>
                        </Row>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
}
